"use client";

import { motion } from "motion/react";
import { MessageCircle, X } from "lucide-react";
import Image from "next/image";
import { useAppSelector } from "@/store/hooks";
import { ISettings } from "@/slices/homeSlice";
import { useState } from "react";

export function StickyIcons() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasNotification, setHasNotification] = useState(true);
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const WhatsAppNo = Settings?.["WhatsAppNo"]?.SettingValue;
  const TripAdviserLink = Settings?.["TripAdviserLink"]?.SettingValue;
  const BookingDotComLink = Settings?.["BookingDotComLink"]?.SettingValue;

  const phone = WhatsAppNo ? WhatsAppNo.replace(/\D+/g, "") : "";
  const whatsappLink = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent("Hello! I'd like to know more details about Sarasiruham Resort.")}`
    : "#";

  const handleIconClick = () => {
    setIsOpen(!isOpen);
    setHasNotification(false);
  };

  const handleCardClick = () => {
    if (whatsappLink !== "#") {
      window.open(whatsappLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      {/* WhatsApp Dialog Box */}
      <div
        className={`fixed bottom-28 md:bottom-20 right-4 z-[999] w-[320px] md:w-[350px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 transition-all duration-300 origin-bottom-right transform ${
          isOpen
            ? "scale-100 opacity-100 pointer-events-auto"
            : "scale-75 opacity-0 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="bg-[#09D261] p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shadow-inner">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm md:text-base leading-tight tracking-wide">
                Sarasiruham Resort
              </span>
              <span className="text-[10px] md:text-xs text-white/90">
                Welcome to Sarasiruham Resort
              </span>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            className="p-1 hover:bg-white/10 rounded-full transition-colors focus:outline-none"
            aria-label="Close chat dialog"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="bg-[#f5f7f9] p-4">
          <div
            onClick={handleCardClick}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:bg-gray-50 transition-all duration-300 cursor-pointer flex items-center gap-3 group"
          >
            {/* Left green circular WhatsApp logo inside the card */}
            <div className="w-10 h-10 bg-[#25D366]/10 text-[#25D366] rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <MessageCircle className="w-5 h-5" />
            </div>

            {/* Right Text Details */}
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5 text-[11px] text-green-500 font-semibold mb-0.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span>Online</span>
              </div>
              <span className="font-bold text-gray-800 text-sm truncate">
                Sarasiruham Resort
              </span>
              <span className="text-[11px] text-gray-500 mt-0.5 font-medium truncate">
                How can we help you today?
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* WhatsApp Icon */}
      {WhatsAppNo && (
        <motion.button
          type="button"
          onClick={handleIconClick}
          aria-label="Contact us on WhatsApp"
          className="fixed bottom-4 right-4 z-50 bg-[#25D366] text-white w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:shadow-[0_20px_50px_rgba(37,211,102,0.4)] cursor-pointer border-none outline-none"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1, type: "spring" }}
        >
          <MessageCircle className="w-8 h-8" />
          {/* Notification Badge */}
          {hasNotification && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white animate-pulse shadow-md">
              1
            </span>
          )}
        </motion.button>
      )}

      {/* Booking.com Link */}
      {BookingDotComLink && (
        <motion.a
          href={BookingDotComLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View our Booking.com reviews"
          className="fixed bottom-24 right-4 z-50 flex items-center justify-center w-[81px] h-[81px] rounded-lg shadow-2xl"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1, type: "spring" }}
        >
          <iframe
            src="https://badge.hotelstatic.com/?position=bottom-right&amp;size=81&amp;clickable=true&amp;url=https%3A%2F%2Fwww.booking.com%2Fhotel%2Fin%2Fsarasiruham-resort.html"
            scrolling="no"
            title="Booking.com Badge"
            style={{
              borderStyle: "none",
              overflow: "hidden",
              width: "81px",
              height: "81px",
              pointerEvents: "none",
            }}
          ></iframe>
        </motion.a>
      )}

      {/* TripAdvisor Logo */}
      {TripAdviserLink && (
        <motion.a
          href={TripAdviserLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View our TripAdvisor reviews"
          className="fixed bottom-48 right-4 z-50 bg-[#538f3f] w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:shadow-3xl p-2"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.2, type: "spring" }}
        >
          <div className="relative w-full h-full">
            <Image
              src="/imports/image-5.png"
              alt="TripAdvisor"
              fill
              sizes="64px"
              className="object-contain"
            />
          </div>
        </motion.a>
      )}
    </>
  );
}
